import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Typography } from 'Components/atoms';
import { DropdownComponent } from 'Components/molecules';
import type { FilterModel, GridApi, SetFilterModel } from 'ag-grid-community';
import { useI18n } from 'Hooks/useI18n';
import { useEarlyAccess } from 'Hooks/useEarlyAccess';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { Row } from 'Core-utils/types';
import { UserCohort } from 'Components/organisms/UserCohort';
import { ALL_USERS_COHORT } from 'Components/organisms/UserCohort/Data';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { KIND, CustomActionDto } from '@ampsec/platform-client';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import {
    createPhishingCampaign,
    getPhishingCampaignMetrics,
    getPhishingCampaignRecipients,
    listPhishingCampaigns,
    sendPhishingCampaign,
    getAllActions,
} from 'Apis/library';
import { PHISHING_SAMPLE_CONTEXT, PHISHING_TEMPLATES, PHISHING_TEMPLATE_TOKENS } from './Data';
import './styles.scss';
import { TrainingAndCoachingTabs } from 'Components/organisms/TrainingAndCoachingTabs';
import {
  PhishingCampaignManagementAgGrid,
  PhishingCampaignRecipientsAgGrid,
} from './PhishingCampaignsAgGrids';
import { RECIPIENT_STATUS_OPTIONS, STATUS_FILTER_OPTIONS } from './phishingCampaignsTable.constants';
import { HtmlContent } from 'Components/atoms/HtmlContent/HtmlContent';
import { decodeHtmlFromTransport } from 'Core-utils/helpers/transportEncoding';
import type { PhishingCampaignRow, PhishingRecipientRow } from './phishingCampaignsTable.types';

/**
 * Ag Grid CSV export: `forEachNodeAfterFilter*` can return no nodes (pagination / row tree timing),
 * so we mirror the recipients grid — search bar + status set filter — over `recipients` row data.
 */
function applyPhishingRecipientFiltersForCsvExport(
    rows: PhishingRecipientRow[],
    searchQuery: string,
    filterModel: FilterModel | null | undefined,
    getStatusLabel: (status: string) => string,
): PhishingRecipientRow[] {
    const q = searchQuery.trim().toLowerCase();
    let out = rows;
    if (q) {
        out = out.filter(
            (d) =>
                d.name.toLowerCase().includes(q) ||
                d.email.toLowerCase().includes(q) ||
                d.department.toLowerCase().includes(q) ||
                d.organization.toLowerCase().includes(q),
        );
    }

    const statusRaw = filterModel?.status;
    if (!statusRaw || typeof statusRaw !== 'object' || !Array.isArray((statusRaw as SetFilterModel).values)) {
        return out;
    }
    const values = (statusRaw as SetFilterModel).values.filter(
        (v): v is string => typeof v === 'string' && v !== '',
    );
    if (values.length === 0) {
        return [];
    }
    const allowed = new Set(values);
    return out.filter((d) => allowed.has(getStatusLabel(d.status)));
}

type PhishingCampaign = {
    id: string;
    name: string;
    subject: string;
    body: string;
    status: 'draft' | 'sent' | 'scheduled';
    workflowStatus?: 'processing' | 'ready' | 'sent' | 'failed';
    sentAt?: string | null;
    scheduledAt?: string | null;
    createdAt?: string;
};

type CampaignMetrics = {
    total: number;
    sent: number;
    opened: number;
    clicked: number;
    reported: number;
};

const defaultMetrics: CampaignMetrics = { total: 0, sent: 0, opened: 0, clicked: 0, reported: 0 };
const CUSTOM_TEMPLATES_STORAGE_KEY = 'phishingTemplates.custom';
const AI_TEMPLATE_ID = 'ai-generated';
const DUMMY_LINK_HTML = '<a href="#" style="color:#0078D4;text-decoration:underline;">[LINK]</a>';


// Robust fallback context to ensure previews never crash
const LOCAL_SAMPLE_CONTEXT: Record<string, string> = {
    firstName: 'FirstName',
    lastName: 'LastName',
    email: 'user@example.com',
    department: 'Department',
    organization: 'Organization',
    title: 'Title',
    campaignName: 'Test Campaign',
    reportLink: '#',
    link: '#',
    ...(typeof PHISHING_SAMPLE_CONTEXT === 'object' && PHISHING_SAMPLE_CONTEXT !== null ? PHISHING_SAMPLE_CONTEXT : {})
};

type CustomTemplate = {
    id: string;
    name: string;
    subject: string;
    body: string;
    department: string;
};

const loadCustomTemplates = (): CustomTemplate[] => {
    try {
        const raw = localStorage.getItem(CUSTOM_TEMPLATES_STORAGE_KEY);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const saveCustomTemplates = (templates: CustomTemplate[]) => {
    try {
        localStorage.setItem(CUSTOM_TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
    } catch {
        // ignore storage errors
    }
};

const PhishingCampaignsPage = () => {
    const { t } = useI18n();
    const { isDevTier, isLoading: earlyAccessLoading } = useEarlyAccess();
    const { addToast } = useContext(ToastsContext);
    const { connectorsByCategoryGroup } = useConnectorData();
    const [rows, setRows] = useState<Row[]>([]);

    const [currentStep, setCurrentStep] = useState(1);
    const [previewMode, setPreviewMode] = useState<'outlook' | 'gmail'>('outlook');

    const [name, setName] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [templateId, setTemplateId] = useState(AI_TEMPLATE_ID);
    const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
    const [sendNow, setSendNow] = useState(true);
    const [scheduledAt, setScheduledAt] = useState('');
    const [scheduledEnd, setScheduledEnd] = useState('');
    const [actionId, setActionId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [trainingCampaign, setTrainingCampaign] = useState(false);

    const [campaigns, setCampaigns] = useState<PhishingCampaign[]>([]);
    const [metricsById, setMetricsById] = useState<Record<string, CampaignMetrics>>({});
    const [loading, setLoading] = useState(false);
    const [, setSearchQuery] = useState('');
    const [selectedResultsCampaignId, setSelectedResultsCampaignId] = useState('');
    const campaignDropdownItems = useMemo(
        () => campaigns.map((c) => ({ id: c.id, value: c.id, displayValue: c.name })),
        [campaigns],
    );
    const selectedCampaignName = useMemo(
        () => campaigns.find((c) => c.id === selectedResultsCampaignId)?.name ?? '',
        [campaigns, selectedResultsCampaignId],
    );

    const [recipients, setRecipients] = useState<PhishingRecipientRow[]>([]);
    const [recipientsLoading, setRecipientsLoading] = useState(false);
    const [recipientsSearch, setRecipientsSearch] = useState('');
    const recipientsGridApiRef = useRef<GridApi<PhishingRecipientRow> | null>(null);
    const [emailBodyModal, setEmailBodyModal] = useState<{
        show: boolean;
        body: string;
        subject: string;
        recipientContext: Record<string, string>;
    }>({ show: false, body: '', subject: '', recipientContext: {} });
    const [modalPreviewMode, setModalPreviewMode] = useState<'outlook' | 'gmail'>('outlook');

    const handleRecipientsGridApi = useCallback((api: GridApi<PhishingRecipientRow> | null) => {
        recipientsGridApiRef.current = api;
    }, []);

    const handleViewEmailBody = useCallback((body: string, row: PhishingRecipientRow) => {
        const campaign = campaigns.find((c) => c.id === selectedResultsCampaignId);
        // Build a context from the actual recipient so {{firstName}} etc. resolve
        // to the real person's data rather than the sample "Taylor" placeholder.
        const nameParts = (row.name ?? '').trim().split(/\s+/);
        // Auto-detect any {{...link...}} placeholder in the body and map it to a
        // dummy anchor so the preview never shows raw tokens.
        const linkPlaceholderRe = /{{\s*([A-Za-z0-9_]*[Ll]ink[A-Za-z0-9_]*)\s*}}/g;
        const autoLinkContext: Record<string, string> = {};
        let linkMatch;
        while ((linkMatch = linkPlaceholderRe.exec(body)) !== null) {
            autoLinkContext[linkMatch[1]] = DUMMY_LINK_HTML;
        }

        const recipientContext: Record<string, string> = {
            ...LOCAL_SAMPLE_CONTEXT,
            firstName: nameParts[0] ?? '',
            lastName: nameParts.slice(1).join(' ') ?? '',
            email: row.email ?? '',
            department: row.department ?? '',
            organization: row.organization ?? '',
            title: row.title ?? '',
            // Merge auto-detected link placeholders (link, reportLink, trackingLink, etc.)
            ...autoLinkContext,
        };
        setEmailBodyModal({ show: true, body, subject: campaign?.subject ?? '', recipientContext });
        setModalPreviewMode('outlook');
    }, [campaigns, selectedResultsCampaignId]);

    const handleCloseEmailBodyModal = useCallback(() => {
        setEmailBodyModal({ show: false, body: '', subject: '', recipientContext: {} });
    }, []);

    const isAiTemplate = templateId === AI_TEMPLATE_ID;

    const { data: actionsData } = useApiObjectData<CustomActionDto[], CustomActionDto[]>(
        KIND.CUSTOM_ACTIONS,
        getAllActions,
        (a) => a,
        { isTemplate: true, limit: 250 },
    );

    const steps = [
        { number: 1, labelKey: 'PHISHING_STEP_CONFIGURE', defaultLabel: 'Configure' },
        { number: 2, labelKey: 'PHISHING_STEP_SCHEDULE', defaultLabel: 'Schedule' },
    ];

    const isStep1Valid = useMemo(() => {
        const isNameValid = name.trim().length > 0;
        const isContentValid = isAiTemplate ? true : subject.trim().length > 0 && body.trim().length > 0;
        return isNameValid && isContentValid;
    }, [name, isAiTemplate, subject, body]);

    const handleNext = () => {
        if (currentStep === 1 && !isStep1Valid) {
            return;
        }
        setCurrentStep((p) => p + 1);
    };

    const handleBack = () => {
        setCurrentStep((p) => Math.max(1, p - 1));
    };

    const label = (key: string, fallback: string) => {
        const value = t(key);
        return value && value !== key ? value : fallback;
    };

    const formatDate = (value?: string | null) => {
        return value ? new Date(value).toLocaleString() : '-';
    };

    const getStatusLabel = (status: string) => {
        const normalized = status?.toLowerCase() ?? 'pending';
        switch (normalized) {
            case 'reported':
                return 'Reported';
            case 'failed':
                return 'Failed';
            case 'opened':
                return 'Opened';
            case 'sent':
                return 'Sent';
            default:
                return 'Pending';
        }
    };

    const getCampaignStatusLabel = (status: string) => {
        const normalized = status?.toLowerCase() ?? 'draft';
        switch (normalized) {
            case 'processing':
                return 'Processing';
            case 'ready':
                return 'Ready';
            case 'sent':
                return 'Sent';
            case 'scheduled':
                return 'Scheduled';
            case 'failed':
                return 'Failed';
            default:
                return 'Draft';
        }
    };

    const campaignRows = useMemo<PhishingCampaignRow[]>(() => {
        return campaigns.map((campaign) => {
            const metrics = metricsById[campaign.id] ?? defaultMetrics;
            const sentAt = campaign.sentAt ? new Date(campaign.sentAt).toLocaleString() : '-';
            const scheduledAt = campaign.scheduledAt ? new Date(campaign.scheduledAt).toLocaleString() : '-';
            const sentAtMs = campaign.sentAt ? new Date(campaign.sentAt).getTime() : 0;
            const scheduledAtMs = campaign.scheduledAt ? new Date(campaign.scheduledAt).getTime() : 0;
            const statusSource = campaign.workflowStatus ?? campaign.status;
            return {
                id: campaign.id,
                name: campaign.name,
                subject: campaign.subject,
                status: getCampaignStatusLabel(statusSource),
                sentAt,
                sentAtMs,
                scheduledAt,
                scheduledAtMs,
                total: metrics.total,
                sent: metrics.sent,
                opened: metrics.opened,
                clicked: metrics.clicked,
                reported: metrics.reported,
            };
        });
    }, [campaigns, metricsById]);

    useEffect(() => {
        if (rows.length === 0 || (rows[0]?.autoCompleteData?.length ?? 0) === 0) {
            const imageUrl = connectorsByCategoryGroup?.['Identity & Access']?.[0]?.imageUrl;
            setRows(ALL_USERS_COHORT(imageUrl));
        }
    }, [rows, connectorsByCategoryGroup]);

    useEffect(() => {
        setCustomTemplates(loadCustomTemplates());
    }, []);

    useEffect(() => {
        saveCustomTemplates(customTemplates);
    }, [customTemplates]);

    const selectedDepartments = useMemo(() => {
        const departments = rows
            .flatMap((row) => row.autoCompleteData)
            .filter((item) => item.kind === 'DEPARTMENT' && item.inclusive)
            .map((item) => item.value.toLowerCase());
        return new Set(departments);
    }, [rows]);

    const allTemplates = useMemo(() => {
        const custom = customTemplates.map((template) => ({
            ...template,
            department: template.department || 'Custom',
        }));
        return [...PHISHING_TEMPLATES, ...custom];
    }, [customTemplates]);

    const filteredTemplates = useMemo(() => {
        if (selectedDepartments.size === 0) {
            return allTemplates;
        }
        return allTemplates.filter((template) => {
            const department = template.department?.toLowerCase() ?? '';
            return template.id === AI_TEMPLATE_ID || department === 'custom' || selectedDepartments.has(department);
        });
    }, [allTemplates, selectedDepartments]);

    const templateOptions = useMemo(() => {
        return [
            {
                id: 'custom',
                value: 'custom',
                displayValue: label('PHISHING_TEMPLATE_CUSTOM_LABEL', 'Custom'),
            },
            ...filteredTemplates.map((template) => ({
                id: template.id,
                value: template.id,
                displayValue: `${template.department} — ${template.name}`,
            })),
        ];
    }, [filteredTemplates, label]);

    const selectedTemplateDisplayValue = useMemo(() => {
        return templateOptions.find((opt) => opt.value === templateId)?.displayValue || '';
    }, [templateOptions, templateId]);

    useEffect(() => {
        if (selectedDepartments.size === 0) {
            return;
        }
        if (templateId === AI_TEMPLATE_ID) {
            return;
        }
        const currentTemplate = allTemplates.find((t) => t.id === templateId);
        const currentDepartment = (currentTemplate?.department ?? '').toLowerCase();
        const currentIsValid =
            currentTemplate &&
            (currentTemplate.id === AI_TEMPLATE_ID || currentDepartment === 'custom' || selectedDepartments.has(currentDepartment));
        if (templateId === 'custom') {
            const firstMatch = allTemplates.find((template) =>
                selectedDepartments.has((template.department ?? '').toLowerCase()),
            );
            if (firstMatch) {
                setTemplateId(firstMatch.id);
            }
            return;
        }
        if (currentIsValid) {
            return;
        }
        const firstMatch = allTemplates.find((template) =>
            selectedDepartments.has((template.department ?? '').toLowerCase()),
        );
        if (firstMatch) {
            setTemplateId(firstMatch.id);
        }
    }, [selectedDepartments, allTemplates, templateId]);

    // This handles updating the inputs when a new template is selected from the dropdown
    useEffect(() => {
        if (templateId === 'custom') {
            return;
        }
        if (templateId === AI_TEMPLATE_ID) {
            setSubject('');
            setBody('');
            return;
        }
        const template = allTemplates.find((t) => t.id === templateId);
        if (template) {
            setSubject(template.subject || '');
            setBody(template.body || '');
        }
    }, [templateId, allTemplates]);

    useEffect(() => {
        if (!isAiTemplate && trainingCampaign) {
            setTrainingCampaign(false);
        }
    }, [isAiTemplate, trainingCampaign]);

    const getSelectedUserCohorts = () => {
        return rows
            .flatMap((row) => row.autoCompleteData)
            .filter((dataItem) => dataItem.id !== '0')
            .map((item) => {
                const tempItem = item.kind === 'USER' ? { ...item, value: item.id } : item;
                const inclusive = typeof tempItem.inclusive === 'boolean' ? tempItem.inclusive : true;
                const kind = tempItem.kind ?? '';
                const value = tempItem.value ?? '';
                return { kind, value: String(value), inclusive };
            });
    };

    const loadCampaigns = async () => {
        setLoading(true);
        try {
            const data = await listPhishingCampaigns();
            setCampaigns(data);
            const metricResults = await Promise.all(
                (data || []).map(async (campaign: PhishingCampaign) => {
                    const metrics = await getPhishingCampaignMetrics(campaign.id);
                    return { id: campaign.id, metrics };
                }),
            );
            const metricsMap: Record<string, CampaignMetrics> = {};
            metricResults.forEach((item) => {
                metricsMap[item.id] = item.metrics ?? defaultMetrics;
            });
            setMetricsById(metricsMap);
        } catch (err: any) {
            addToast({
                severity: 'error',
                body: err?.response?.data?.message || 'Failed to load phishing campaigns.',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCampaigns();
    }, []);

    useEffect(() => {
        if (!selectedResultsCampaignId && campaigns.length > 0) {
            setSelectedResultsCampaignId(campaigns[0].id);
        }
    }, [campaigns, selectedResultsCampaignId]);

    useEffect(() => {
        const loadRecipients = async () => {
            if (!selectedResultsCampaignId) {
                setRecipients([]);
                return;
            }
            setRecipientsSearch('');
            setRecipientsLoading(true);
            try {
                const response = await getPhishingCampaignRecipients(selectedResultsCampaignId);
                const data = Array.isArray(response?.data) ? response.data : [];
                const campaignSubject =
                    campaigns.find((c) => c.id === selectedResultsCampaignId)?.subject ?? '';
                const rows: PhishingRecipientRow[] = data.map((item: any) => ({
                    id: item.id ?? '',
                    uid: item.uid ?? '',
                    name: item.displayName ?? item.name ?? '',
                    email: item.email ?? '',
                    subject: campaignSubject,
                    emailBody: decodeHtmlFromTransport(item.emailBody ?? ''),
                    department: item.department ?? '',
                    organization: item.organization ?? '',
                    title: item.title ?? '',
                    status: item.status ?? 'pending',
                    sentAt: formatDate(item.sentAt),
                    sentAtMs: item.sentAt ? new Date(item.sentAt).getTime() : 0,
                    openedAt: formatDate(item.openedAt),
                    openedAtMs: item.openedAt ? new Date(item.openedAt).getTime() : 0,
                    clickedAt: formatDate(item.clickedAt),
                    clickedAtMs: item.clickedAt ? new Date(item.clickedAt).getTime() : 0,
                    reportedAt: formatDate(item.reportedAt),
                    reportedAtMs: item.reportedAt ? new Date(item.reportedAt).getTime() : 0,
                }));
                setRecipients(rows);
            } catch (err: any) {
                addToast({
                    severity: 'error',
                    body: err?.response?.data?.message || 'Failed to load campaign results.',
                });
                setRecipients([]);
            } finally {
                setRecipientsLoading(false);
            }
        };
        loadRecipients();
    }, [selectedResultsCampaignId]);

    const handleSaveCustomTemplate = () => {
        if (!subject.trim() || !body.trim()) {
            addToast({
                severity: 'error',
                body: 'Subject and body are required to save a custom template.',
            });
            return;
        }

        // Use Campaign Name if provided, otherwise generate a default name
        const finalTemplateName = name.trim() || `Custom Template ${new Date().toLocaleTimeString()}`;

        const newTemplate: CustomTemplate = {
            id: `custom-${Date.now()}`,
            name: finalTemplateName,
            department: 'Custom',
            subject: subject.trim(),
            body: body.trim(),
        };
        setCustomTemplates((prev) => [newTemplate, ...prev]);
        setTemplateId(newTemplate.id);
        addToast({ severity: 'success', body: 'Custom template saved successfully.' });
    };

    const renderTemplate = (templateStr: string, context: Record<string, string>) => {
        if (!templateStr) {
            return '';
        }
        return templateStr.replace(/{{\s*([A-Za-z0-9_]+)\s*}}/g, (_match, key) => {
            if (Object.prototype.hasOwnProperty.call(context, key)) {
                return context[key] ?? '';
            }
            return _match;
        });
    };

    const actionOptions = useMemo(() => {
        const baseOptions = [{ id: 'none', value: '', displayValue: label('NONE', 'None') }];
        if (!Array.isArray(actionsData)) {
            return baseOptions;
        }

        return [
            ...baseOptions,
            ...actionsData.map((action) => ({
                id: action.id,
                value: action.id,
                displayValue: action.displayValue || action.id,
            })),
        ];
    }, [actionsData, label]);

    const previewSubject = useMemo(() => {
        if (isAiTemplate) {
            return trainingCampaign
                ? label('PHISHING_AI_TRAINING_PREVIEW_SUBJECT', 'AI training subject will be created per recipient.')
                : label('PHISHING_AI_PREVIEW_SUBJECT', 'AI-generated subject will be created per recipient.');
        }
        const rendered = renderTemplate(subject || '', LOCAL_SAMPLE_CONTEXT);
        return rendered || label('PHISHING_PREVIEW_EMPTY', 'No content yet.');
    }, [subject, isAiTemplate, trainingCampaign, label]);

    const previewHtml = useMemo(() => {
        if (isAiTemplate) {
            return trainingCampaign
                ? label('PHISHING_AI_TRAINING_PREVIEW_BODY', 'AI training body will be created per recipient.')
                : label('PHISHING_AI_PREVIEW_BODY', 'AI-generated body will be created per recipient.');
        }

        if (!body) {
            return label('PHISHING_PREVIEW_EMPTY', 'No content yet.');
        }

        const contextWithLink = {
            ...LOCAL_SAMPLE_CONTEXT,
            link: `<a href="${LOCAL_SAMPLE_CONTEXT.link || '#'}" style="color: #0078D4; text-decoration: underline;">${label('TRACKING_LINK', '[TRACKING LINK]')}</a>`,
        };

        const html = renderTemplate(body, contextWithLink);
        return html.replace(/\n/g, '<br/>');
    }, [body, isAiTemplate, trainingCampaign, label]);

    const handleCreate = async () => {
        const cohorts = getSelectedUserCohorts();
        if (!name.trim() || (!isAiTemplate && (!subject.trim() || !body.trim()))) {
            addToast({ severity: 'error', body: 'Name, subject, and body are required.' });
            return;
        }
        setIsSubmitting(true);
        try {
            await createPhishingCampaign({
                name: name.trim(),
                subject: isAiTemplate ? undefined : subject.trim(),
                body: isAiTemplate ? undefined : body.trim(),
                templateId,
                trainingCampaign: isAiTemplate ? trainingCampaign : undefined,
                cohorts,
                closeActionId: actionId || undefined,
                sendNow,
                scheduledAt: !sendNow && scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
                scheduledEndAt: scheduledEnd ? new Date(scheduledEnd).toISOString() : undefined,
            });
            addToast({
                severity: 'success',
                body: sendNow ? 'Phishing campaign created and sent.' : 'Phishing campaign created.',
            });
            setName('');
            setSubject('');
            setBody('');
            setTemplateId(AI_TEMPLATE_ID);
            setSendNow(true);
            setScheduledAt('');
            setScheduledEnd('');
            setActionId('');
            setTrainingCampaign(false);
            setCurrentStep(1);
            await loadCampaigns();
        } catch (err: any) {
            addToast({
                severity: 'error',
                body: err?.response?.data?.message || 'Failed to create phishing campaign.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSend = async (id: string) => {
        try {
            await sendPhishingCampaign(id);
            addToast({ severity: 'success', body: 'Phishing campaign sent.' });
            await loadCampaigns();
        } catch (err: any) {
            addToast({
                severity: 'error',
                body: err?.response?.data?.message || 'Failed to send phishing campaign.',
            });
        }
    };

    const escapeCsv = (value: string | number) => {
        return `"${String(value).replace(/"/g, '""')}"`;
    };

    const handleDownloadRecipients = () => {
        const headers = [
            'Name',
            'Email',
            'Department',
            'Organization',
            'Title',
            'Status',
            'Sent At',
            'Opened At',
            'Clicked At',
            'Reported At',
        ];
        const sourceRows = applyPhishingRecipientFiltersForCsvExport(
            recipients,
            recipientsSearch,
            recipientsGridApiRef.current?.getFilterModel() ?? undefined,
            getStatusLabel,
        );
        const rows = sourceRows.map((row) =>
            [
                row.name,
                row.email,
                row.department,
                row.organization,
                row.title,
                getStatusLabel(row.status),
                row.sentAt,
                row.openedAt,
                row.clickedAt,
                row.reportedAt,
            ].map(escapeCsv).join(','),
        );
        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `phishing-campaign-${selectedResultsCampaignId}-results.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    if (earlyAccessLoading) {
        return (
            <div className="d-flex flex-column engagement-flow__content phishing-campaigns-page mt-4">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <Spinner animation="border" />
                </div>
            </div>
        );
    }

    if (!isDevTier) {
        return (
            <div className="d-flex flex-column engagement-flow__content phishing-campaigns-page mt-4">
                <div className="engagement-flow__tab-cont d-flex justify-content-between">
                  <TrainingAndCoachingTabs />
                </div>
                <div className="bg-text-black p-4 card phishing-campaigns-page__card">
                    <Typography variant="subtitle2" color="text-high-emphasis">
                        {label('PHISHING_CAMPAIGNS_TITLE', 'Phishing campaigns')}
                    </Typography>
                    <Typography variant="body3" color="text-medium-emphasis" className="mt-2">
                        {label(
                            'PHISHING_CAMPAIGNS_EXPERIMENTAL_REQUIRED',
                            'This feature is only available for customers with experimental tier access. Please contact your administrator to enable experimental features.',
                        )}
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <>
        <div className="d-flex flex-column engagement-flow__content phishing-campaigns-page mt-4">
            <div className="engagement-flow__tab-cont d-flex justify-content-between mb-4">
                <TrainingAndCoachingTabs />
            </div>

            <div className="row g-3">
                <div className="col-lg-7" style={{ minWidth: 0 }}>
                    <div className="bg-text-black p-4 card phishing-campaigns-page__card h-100 d-flex flex-column" style={{ minWidth: 0 }}>
                        <Typography variant="h6" color="text-high-emphasis" className="mb-4 text-uppercase" style={{ fontSize: '18px', letterSpacing: '1px' }}>
                            {label('PHISHING_CONFIGURATION_TITLE', 'Campaign Builder')}
                        </Typography>

                        {/* styled Stepper Container */}
                        <div className="d-flex align-items-center mb-4 px-1">
                            {steps.map((step, idx) => {
                                const isActive = step.number === currentStep;
                                const isCompleted = step.number < currentStep;

                                return (
                                    <React.Fragment key={step.number}>
                                        <div
                                            className="phishing-campaigns-page__stepper-step"
                                            onClick={() => setCurrentStep(step.number)}
                                        >
                                            <div
                                                className={`phishing-campaigns-page__stepper-circle rounded-circle d-flex align-items-center justify-content-center me-2 ${
                                                    isActive || isCompleted ? 'phishing-campaigns-page__stepper-circle--active' : ''
                                                }`}
                                            >
                                                {step.number}
                                            </div>
                                            <Typography
                                                variant="h6"
                                                className={`phishing-campaigns-page__stepper-label mb-0 whitespace-nowrap text-uppercase ${
                                                    isActive || isCompleted ? 'phishing-campaigns-page__stepper-label--active' : ''
                                                }`}
                                            >
                                                {label(step.labelKey, step.defaultLabel)}
                                            </Typography>
                                        </div>
                                        {/* Fixed width connector line */}
                                        {idx < steps.length - 1 && (
                                            <div className="phishing-campaigns-page__stepper-connector mx-3"></div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        <div className="phishing-campaigns-page__form flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
                            {currentStep === 1 && (
                                <div className="d-flex flex-column flex-grow-1 animation-fade-in m-0" style={{ minWidth: 0 }}>

                                    <div className="mb-4 pb-4 border-bottom" style={{ borderColor: '#3f3f46', minWidth: 0 }}>
                                        <Typography variant="subtitle1" color="text-high-emphasis" className="mb-2 d-block text-uppercase" style={{ fontSize: '14px', letterSpacing: '0.5px' }}>
                                            {label('PHISHING_RECIPIENTS_LABEL', 'User Cohort')}
                                        </Typography>
                                        <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
                                            <UserCohort rows={rows} setRows={setRows} />
                                        </div>
                                    </div>

                                    {/* Split Bottom Section */}
                                    <div className="row flex-grow-1 m-0" style={{ minWidth: 0 }}>
                                        {/* Left Column: Campaign Name */}
                                        <div className="phishing-campaigns-page__left-column col-5 pe-4" style={{ minWidth: 0 }}>
                                            <Form.Group>
                                                <Typography variant="subtitle1" color="text-high-emphasis" className="mb-2 d-block text-uppercase" style={{ fontSize: '14px', letterSpacing: '0.5px' }}>
                                                    {label('CAMPAIGN_NAME_LABEL', 'Campaign Name')}
                                                </Typography>
                                                <Form.Control
                                                    type="text"
                                                    value={name}
                                                    onChange={(ev) => setName(ev.target.value)}
                                                    placeholder="Q1 Phishing Test"
                                                    isInvalid={name.trim().length === 0}
                                                    className="phishing-campaigns-page__input w-100"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {label('CAMPAIGN_NAME_REQUIRED', 'Campaign name is required.')}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </div>

                                        {/* Right Column: Template & Content */}
                                        <div className="col-7 ps-4" style={{ minWidth: 0 }}>
                                            <Typography variant="h6" color="text-high-emphasis" className="mb-4 d-block text-uppercase" style={{ fontSize: '18px', letterSpacing: '1px' }}>
                                                {label('PHISHING_DESIGN_SECTION', 'Design')}
                                            </Typography>
                                            <div className="mb-4">
                                                <Typography variant="subtitle1" color="text-high-emphasis" className="mb-2 d-block text-uppercase" style={{ fontSize: '14px', letterSpacing: '0.5px' }}>
                                                    {label('PHISHING_TEMPLATE_LABEL', 'Template')}
                                                </Typography>
                                                <div style={{ height: '2.5rem' }}>
                                                    <DropdownComponent
                                                        value={selectedTemplateDisplayValue}
                                                        dropDownItems={templateOptions}
                                                        onChange={(val: string | null) => val && setTemplateId(val)}
                                                        isStartCase={false}
                                                        variant="filled"
                                                    />
                                                </div>
                                            <div className="text-text-low-emphasis mt-2" style={{ fontSize: '12px', lineHeight: '1.4', wordBreak: 'break-word' }}>
                                                Available tokens: <span className="text-primary">{PHISHING_TEMPLATE_TOKENS.join(', ')}</span>
                                                </div>
                                                {isAiTemplate && (
                                                    <div className="text-text-low-emphasis mt-1" style={{ fontSize: '12px' }}>
                                                        {label(
                                                            'PHISHING_AI_TEMPLATE_HELP',
                                                            'AI will generate subject and body per user profile fields.',
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <Form.Group className="mb-4 d-flex align-items-center">
                                                <Form.Check
                                                    type="switch"
                                                    id="learning-guidance-switch"
                                                    checked={trainingCampaign}
                                                    onChange={(e) => setTrainingCampaign(e.target.checked)}
                                                    label={
                                                        <span className="ms-2 fw-semibold text-white" style={{ fontSize: '14px', cursor: 'pointer' }}>
                                                            {label('PHISHING_TRAINING_TOGGLE_LABEL', 'Add Learning Guidance')}
                                                        </span>
                                                    }
                                                />
                                            </Form.Group>

                                            <Form.Group className="mb-4">
                                                <Typography variant="subtitle1" color="text-high-emphasis" className="mb-2 d-block text-uppercase" style={{ fontSize: '14px', letterSpacing: '0.5px' }}>
                                                    {label('CAMPAIGN_SUBJECT_LABEL', 'Subject')}
                                                </Typography>
                                                <Form.Control
                                                    type="text"
                                                    value={subject}
                                                    onChange={(ev) => setSubject(ev.target.value)}
                                                    placeholder="Updated PTO Policy"
                                                    disabled={isAiTemplate}
                                                    className="phishing-campaigns-page__input"
                                                />
                                            </Form.Group>

                                            <Form.Group className="mb-4">
                                                <Typography variant="subtitle1" color="text-high-emphasis" className="mb-2 d-block text-uppercase" style={{ fontSize: '14px', letterSpacing: '0.5px' }}>
                                                    {label('CAMPAIGN_BODY_LABEL', 'Body')}
                                                </Typography>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={4}
                                                    value={body}
                                                    onChange={(ev) => setBody(ev.target.value)}
                                                    placeholder="Please review the updated policy: {{link}}"
                                                    disabled={isAiTemplate}
                                                    className="phishing-campaigns-page__input phishing-campaigns-page__input--no-resize"
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 2 && (
                                <div className="animation-fade-in flex-grow-1 p-4">
                                    <Typography variant="h6" color="text-high-emphasis" className="mb-4 d-block text-uppercase" style={{ fontSize: '18px', letterSpacing: '1px' }}>
                                        {label('PHISHING_SCHEDULE_SECTION', 'Schedule Settings')}
                                    </Typography>
                                    <Typography variant="body3" color="text-medium-emphasis" className="mb-4 d-block">
                                        {label('PHISHING_SCHEDULE_INSTRUCTIONS', 'Define when the campaign concludes and select an optional automated action to execute upon completion.')}
                                    </Typography>

                                    <div className="w-50">
                                        <Form.Group className="mb-4 animation-fade-in">
                                            <Form.Label className="phishing-campaigns-page__form-label">
                                                {label('PHISHING_SCHEDULE_END_LABEL', 'Schedule end')}
                                            </Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                value={scheduledEnd}
                                                onChange={(ev) => setScheduledEnd(ev.target.value)}
                                                className="phishing-campaigns-page__input w-100"
                                            />
                                        </Form.Group>

                                        <div className="mb-4">
                                            <Typography variant="caption1" color="text-high-emphasis" className="mb-2 d-block text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                                {label('PHISHING_CUSTOM_ACTION_LABEL', 'Custom Action')}
                                            </Typography>
                                            <div style={{ height: '2.5rem' }}>
                                                <DropdownComponent
                                                    value={actionOptions.find((opt) => opt.value === actionId)?.displayValue || label('NONE', 'None')}
                                                    dropDownItems={actionOptions}
                                                    onChange={(val: string | null) => setActionId(val || '')}
                                                    isStartCase={false}
                                                    variant="filled"
                                                />
                                            </div>
                                        </div>

                                        <Form.Group className="mb-4 d-flex align-items-center">
                                            <Form.Check
                                                type="switch"
                                                id="send-now-switch"
                                                checked={sendNow}
                                                onChange={(e) => setSendNow(e.target.checked)}
                                                label={
                                                    <span className="ms-2 fw-semibold text-white" style={{ fontSize: '14px', cursor: 'pointer' }}>
                                                        {label('PHISHING_SEND_NOW_LABEL', 'Send now')}
                                                    </span>
                                                }
                                            />
                                        </Form.Group>

                                        {!sendNow && (
                                            <Form.Group className="mb-4 animation-fade-in">
                                                <Form.Label className="phishing-campaigns-page__form-label">
                                                    {label('PHISHING_SCHEDULE_LABEL', 'Schedule send')}
                                                </Form.Label>
                                                <Form.Control
                                                    type="datetime-local"
                                                    value={scheduledAt}
                                                    onChange={(ev) => setScheduledAt(ev.target.value)}
                                                    className="phishing-campaigns-page__input w-100"
                                                />
                                            </Form.Group>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Standardized Bottom Right Navigation Controls */}
                            <div className="phishing-campaigns-page__stepper-footer d-flex justify-content-end align-items-center mt-auto pt-3 mt-4">
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleBack}
                                    disabled={currentStep === 1}
                                    className={`phishing-campaigns-page__btn--back me-2 ${currentStep === 1 ? 'd-none' : ''}`}
                                >
                                    {label('BACK_LABEL', 'Back')}
                                </Button>
                                {currentStep < 2 && (
                                    <Button
                                        variant="outline-primary"
                                        onClick={handleNext}
                                        disabled={!isStep1Valid}
                                        className="phishing-campaigns-page__btn--next"
                                    >
                                        {label('NEXT_LABEL', 'Next Step')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column - Live Preview & Action Buttons */}
                <div className="col-lg-5" style={{ minWidth: 0 }}>
                    <div className="d-flex flex-column h-100">
                        {/* Interactive Dynamic Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Typography variant="subtitle2" color="text-high-emphasis" className="text-uppercase fw-bold m-0">
                                {label('PHISHING_PREVIEW_TITLE', 'LIVE PREVIEW')}
                            </Typography>
                            <div className="phishing-campaigns-page__preview-toggle-group d-flex bg-dark rounded-pill p-1">
                                <Button
                                    variant="link"
                                    size="sm"
                                    className={`phishing-campaigns-page__preview-toggle-btn rounded-pill px-3 py-1 text-decoration-none ${previewMode === 'outlook' ? 'phishing-campaigns-page__preview-toggle-btn--active' : 'text-muted'}`}
                                    onClick={() => setPreviewMode('outlook')}
                                >
                                    Outlook
                                </Button>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className={`phishing-campaigns-page__preview-toggle-btn rounded-pill px-3 py-1 text-decoration-none ${previewMode === 'gmail' ? 'phishing-campaigns-page__preview-toggle-btn--active' : 'text-muted'}`}
                                    onClick={() => setPreviewMode('gmail')}
                                >
                                    Gmail
                                </Button>
                            </div>
                        </div>

                        {/* Dynamic Email Mockup */}
                        <div className="phishing-campaigns-page__preview phishing-campaigns-page__preview-mockup d-flex flex-column flex-grow-1 rounded overflow-hidden">
                            {previewMode === 'outlook' ? (
                                <>
                                    {/* Outlook Header */}
                                    <div className="phishing-campaigns-page__preview-header-outlook d-flex justify-content-between align-items-center p-2 px-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="fs-5 bg-white text-primary rounded px-1 fw-bold" style={{ lineHeight: 1 }}>O</span>
                                            <span className="fs-6">Outlook</span>
                                        </div>
                                        <div className="d-flex gap-3 align-items-center" style={{ fontSize: '10px' }}>
                                            <span>—</span>
                                            <span>□</span>
                                            <span>✕</span>
                                        </div>
                                    </div>

                                    <div className="phishing-campaigns-page__preview-text-black px-3 py-3 border-bottom" style={{ fontSize: '0.9rem' }}>
                                        <div className="d-flex mb-3 align-items-center">
                                            <span className="text-muted me-3" style={{ minWidth: '50px' }}>Subject:</span>
                                            <span className="phishing-campaigns-page__preview-text-black fw-semibold">{previewSubject}</span>
                                        </div>
                                        <div className="d-flex mb-3 align-items-center">
                                            <span className="text-muted me-3" style={{ minWidth: '50px' }}>From:</span>
                                            <div className="phishing-campaigns-page__preview-placeholder-pill rounded-pill border" style={{ width: '200px', height: '15px' }}></div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="text-muted me-3" style={{ minWidth: '50px' }}>To:</span>
                                            <div className="bg-light border rounded px-2 py-1 text-muted" style={{ fontSize: '0.85rem' }}>FirstName LastName ((pills))</div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Gmail Header */}
                                    <div className="phishing-campaigns-page__preview-header-gmail px-4 py-3 border-bottom">
                                        <div className="fs-5 mb-3 fw-normal" style={{ fontFamily: 'Google Sans, Roboto, sans-serif' }}>
                                            {previewSubject} <span className="phishing-campaigns-page__preview-gmail-badge badge bg-secondary ms-2" style={{ fontSize: '0.6rem', fontWeight: 'normal' }}>Inbox</span>
                                        </div>
                                        <div className="d-flex align-items-center mt-3">
                                            <div className="phishing-campaigns-page__preview-gmail-avatar rounded-circle d-flex align-items-center justify-content-center me-3 text-white" style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}>
                                                H
                                            </div>
                                            <div style={{ fontFamily: 'Roboto, sans-serif' }}>
                                                <div className="fw-bold" style={{ fontSize: '0.9rem' }}>HR Department <span className="fw-normal text-muted" style={{ fontSize: '0.8rem' }}>&lt;hr@example.com&gt;</span></div>
                                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>to me <span className="ms-1" style={{ fontSize: '0.6rem' }}>▼</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            <HtmlContent
                                html={previewHtml}
                                className="phishing-campaigns-page__preview-body-content p-4 flex-grow-1"
                                style={{ fontSize: '0.95rem', fontFamily: previewMode === 'gmail' ? 'Roboto, Arial, sans-serif' : 'Segoe UI, Arial, sans-serif' }}
                            />
                        </div>

                        {/* Global Actions decoupled from Stepper */}
                        <div className="mt-3 d-flex flex-column gap-2">
                            <Button
                                variant="primary"
                                className="phishing-campaigns-page__action-btn--create w-100 py-2 fw-bold"
                                onClick={handleCreate}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <Spinner size="sm" /> : label('CREATE_CAMPAIGN_LABEL', 'CREATE CAMPAIGN')}
                            </Button>
                            <Button
                                variant="dark"
                                className="phishing-campaigns-page__action-btn--save w-100 py-2 fw-bold border-0"
                                onClick={handleSaveCustomTemplate}
                                disabled={isAiTemplate || !subject.trim() || !body.trim()}
                            >
                                {label('PHISHING_SAVE_TEMPLATE_LABEL', 'SAVE TEMPLATE')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-text-black p-4 card mt-4 phishing-campaigns-page__card">
                <Typography variant="subtitle2" color="text-high-emphasis" className="mb-3">
                    {label('PHISHING_MANAGEMENT_TITLE', 'Campaign Management')}
                </Typography>

                <div className="mb-5">
                    <PhishingCampaignManagementAgGrid
                        label={label}
                        rowData={campaignRows}
                        loading={loading}
                        onSearchChange={(searchValue) => setSearchQuery(searchValue)}
                        onSend={handleSend}
                        statusFilterValues={STATUS_FILTER_OPTIONS}
                    />
                </div>

                <div className="border-top border-secondary my-4"></div>

                <div>
                    <Typography variant="subtitle2" color="text-high-emphasis" className="mb-3">
                        {label('PHISHING_CAMPAIGNS_RESULTS_TITLE', 'Campaign results')}
                    </Typography>

                    <div className="phishing-campaigns-page__table-toolbar d-flex justify-content-between align-items-center">
                      <div style={{ width: '16rem', height: '2.5rem' }} className="mb-3">
                        <DropdownComponent
                            value={selectedCampaignName}
                            dropDownItems={campaignDropdownItems}
                            onChange={(val) => {
                                if (val) {
                                    setSelectedResultsCampaignId(val);
                                }
                            }}
                            isStartCase={false}
                            variant="filled"
                            placeholder={label('NO_RESULTS_FOUND', 'No results found')}
                            disableToggle={campaigns.length === 0}
                        />
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleDownloadRecipients}
                        disabled={recipients.length === 0}
                        className="text-text-high-emphasis"
                    >
                        {label('DOWNLOAD_CSV_LABEL', 'Download CSV')}
                    </Button>
                    </div>

                    <PhishingCampaignRecipientsAgGrid
                        label={label}
                        rowData={recipients}
                        loading={recipientsLoading}
                        getStatusLabel={getStatusLabel}
                        statusFilterValues={RECIPIENT_STATUS_OPTIONS}
                        onSearchChange={(searchValue) => setRecipientsSearch(searchValue)}
                        onGridApi={handleRecipientsGridApi}
                        onViewEmailBody={handleViewEmailBody}
                    />
                </div>
            </div>
        </div>

        {/*
          Bootstrap Modal handles centering + backdrop natively.
          The inner wrapper carries the page CSS class so that all nested
          SCSS rules (.phishing-campaigns-page__preview, etc.) resolve
          correctly even though the modal portal renders outside the page div.
          contentClassName removes Bootstrap's own white .modal-content card.
        */}
        <Modal
            show={emailBodyModal.show}
            onHide={handleCloseEmailBodyModal}
            size="lg"
            centered
            contentClassName="bg-transparent border-0 shadow-none"
        >
            <Modal.Body className="p-0">
                {/* phishing-campaigns-page restores SCSS nesting context for the portal */}
                <div className="phishing-campaigns-page">
                <div className="phishing-campaigns-page__email-modal-wrapper d-flex flex-column" style={{ gap: '0.75rem' }}>

                    {/* Header row — mirrors the live-preview panel header */}
                    <div className="d-flex justify-content-between align-items-center">
                        <Typography variant="subtitle2" color="text-high-emphasis" className="text-uppercase fw-bold m-0">
                            {label('EMAIL_PREVIEW_MODAL_TITLE', 'Email Preview')}
                        </Typography>
                        <div className="d-flex align-items-center gap-3">
                            <div className="phishing-campaigns-page__preview-toggle-group d-flex bg-dark rounded-pill p-1">
                                <Button
                                    variant="link"
                                    size="sm"
                                    className={`phishing-campaigns-page__preview-toggle-btn rounded-pill px-3 py-1 text-decoration-none ${modalPreviewMode === 'outlook' ? 'phishing-campaigns-page__preview-toggle-btn--active' : 'text-muted'}`}
                                    onClick={() => setModalPreviewMode('outlook')}
                                >
                                    Outlook
                                </Button>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className={`phishing-campaigns-page__preview-toggle-btn rounded-pill px-3 py-1 text-decoration-none ${modalPreviewMode === 'gmail' ? 'phishing-campaigns-page__preview-toggle-btn--active' : 'text-muted'}`}
                                    onClick={() => setModalPreviewMode('gmail')}
                                >
                                    Gmail
                                </Button>
                            </div>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={handleCloseEmailBodyModal}
                                aria-label="Close"
                                style={{ filter: 'invert(1)', opacity: 0.7 }}
                            />
                        </div>
                    </div>

                    {/*
                      Outer dark card (__preview) → inner white email client (__preview-mockup).
                      Keeping them as separate parent/child divs ensures the dark background
                      and traffic-light ::before are visible in the 28px top-padding area of
                      __preview, while __preview-mockup fills the rest with white.
                    */}
                    <div className="phishing-campaigns-page__preview d-flex flex-column" style={{ minHeight: '480px' }}>
                        <div className="phishing-campaigns-page__preview-mockup d-flex flex-column flex-grow-1 rounded overflow-hidden">
                            {modalPreviewMode === 'outlook' ? (
                                <>
                                    <div className="phishing-campaigns-page__preview-header-outlook d-flex justify-content-between align-items-center p-2 px-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="fs-5 bg-white text-primary rounded px-1 fw-bold" style={{ lineHeight: 1 }}>O</span>
                                            <span className="fs-6">Outlook</span>
                                        </div>
                                        <div className="d-flex gap-3 align-items-center" style={{ fontSize: '10px' }}>
                                            <span>—</span>
                                            <span>□</span>
                                            <span>✕</span>
                                        </div>
                                    </div>
                                    <div className="phishing-campaigns-page__preview-text-black px-3 py-3 border-bottom" style={{ fontSize: '0.9rem' }}>
                                        <div className="d-flex mb-3 align-items-center">
                                            <span className="text-muted me-3" style={{ minWidth: '50px' }}>Subject:</span>
                                            <span className="phishing-campaigns-page__preview-text-black fw-semibold">
                                                {renderTemplate(emailBodyModal.subject, emailBodyModal.recipientContext) || label('PHISHING_PREVIEW_EMPTY', 'No subject')}
                                            </span>
                                        </div>
                                        <div className="d-flex mb-3 align-items-center">
                                            <span className="text-muted me-3" style={{ minWidth: '50px' }}>From:</span>
                                            <div className="phishing-campaigns-page__preview-placeholder-pill rounded-pill border" style={{ width: '200px', height: '15px' }}></div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="text-muted me-3" style={{ minWidth: '50px' }}>To:</span>
                                            <div className="bg-light border rounded px-2 py-1 text-muted" style={{ fontSize: '0.85rem' }}>
                                                {emailBodyModal.recipientContext.email || '-'}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="phishing-campaigns-page__preview-header-gmail px-4 py-3 border-bottom">
                                        <div className="fs-5 mb-3 fw-normal" style={{ fontFamily: 'Google Sans, Roboto, sans-serif' }}>
                                            {renderTemplate(emailBodyModal.subject, emailBodyModal.recipientContext) || label('PHISHING_PREVIEW_EMPTY', 'No subject')}
                                            <span className="phishing-campaigns-page__preview-gmail-badge badge bg-secondary ms-2" style={{ fontSize: '0.6rem', fontWeight: 'normal' }}>Inbox</span>
                                        </div>
                                        <div className="d-flex align-items-center mt-3">
                                            <div className="phishing-campaigns-page__preview-gmail-avatar rounded-circle d-flex align-items-center justify-content-center me-3 text-white" style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}>
                                                H
                                            </div>
                                            <div style={{ fontFamily: 'Roboto, sans-serif' }}>
                                                <div className="fw-bold" style={{ fontSize: '0.9rem' }}>HR Department <span className="fw-normal text-muted" style={{ fontSize: '0.8rem' }}>&lt;hr@example.com&gt;</span></div>
                                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                    to {emailBodyModal.recipientContext.email || 'me'}
                                                    <span className="ms-1" style={{ fontSize: '0.6rem' }}>▼</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                            <HtmlContent
                                html={renderTemplate(emailBodyModal.body, emailBodyModal.recipientContext) || label('PHISHING_PREVIEW_EMPTY', 'No content yet.')}
                                className="phishing-campaigns-page__preview-body-content p-4 flex-grow-1 custom-scrollbar"
                                style={{ fontSize: '0.95rem', fontFamily: modalPreviewMode === 'gmail' ? 'Roboto, Arial, sans-serif' : 'Segoe UI, Arial, sans-serif', overflowY: 'auto' }}
                            />
                        </div>
                    </div>

                    <div className="d-flex justify-content-center">
                        <Button
                            variant="outline-text-low-emphasis"
                            className="body1 btn-cancel"
                            onClick={handleCloseEmailBodyModal}
                        >
                            {label('CANCEL', 'Cancel')}
                        </Button>
                    </div>

                </div>
                </div>
            </Modal.Body>
        </Modal>
        </>
    );
};

export default PhishingCampaignsPage;